'use client'

import type {
    DiagnosticHypothesis,
    RedFlag,
    SuggestedExam,
} from '@/lib/api/consultations/consultation-api'
import {
    MdAdd,
    MdBiotech,
    MdCheck,
    MdPriorityHigh,
    MdPsychology,
    MdWarningAmber,
} from 'react-icons/md'

interface Props {
    hypotheses?: DiagnosticHypothesis[]
    suggestedExams?: SuggestedExam[]
    redFlags?: RedFlag[]
    /** Sem `onPickExam`, os exames viram leitura (tela sem paciente no contexto). */
    onPickExam?: (exam: SuggestedExam) => void
    /** Exames já marcados para o pedido, por nome. */
    pickedExams?: string[]
}

const normalize = (value: string): string =>
    (value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()

const LIKELIHOOD_LABEL: Record<DiagnosticHypothesis['likelihood'], string> = {
    alta: 'Probabilidade alta',
    media: 'Probabilidade média',
    baixa: 'Probabilidade baixa',
}

const LIKELIHOOD_CLASS: Record<DiagnosticHypothesis['likelihood'], string> = {
    alta: 'badge-error',
    media: 'badge-warning',
    baixa: 'badge-ghost',
}

/**
 * A leitura clínica da consulta: hipóteses com o raciocínio, exames que mudam a
 * conduta e sinais de alarme.
 *
 * Separado do `AiSummaryCard` porque é outra coisa: aquele resume o que foi DITO,
 * este mostra o que a IA CONCLUIU — e conclusão precisa vir com o porquê ao lado,
 * senão o médico não tem como discordar.
 *
 * Os sinais de alarme vêm primeiro, antes de qualquer outra coisa: se existe algo
 * que não pode esperar, ele não pode estar embaixo de duas listas.
 */
export function ClinicalInsights({
    hypotheses,
    suggestedExams,
    redFlags,
    onPickExam,
    pickedExams,
}: Props) {
    const hasContent =
        (hypotheses?.length ?? 0) > 0 ||
        (suggestedExams?.length ?? 0) > 0 ||
        (redFlags?.length ?? 0) > 0

    if (!hasContent) return null

    const isPicked = (exam: SuggestedExam) =>
        (pickedExams ?? []).some(picked => normalize(picked) === normalize(exam.name))

    return (
        <div className="flex flex-col gap-4">
            {(redFlags?.length ?? 0) > 0 && (
                <section>
                    <div className="mb-2 flex items-center gap-2 text-sm font-medium text-error">
                        <MdPriorityHigh className="text-lg" />
                        Sinais de alarme
                    </div>
                    <ul className="flex flex-col gap-2">
                        {redFlags!.map((flag, index) => (
                            <li
                                key={index}
                                className={`rounded-lg border-l-4 p-3 ${
                                    flag.severity === 'alta'
                                        ? 'border-error bg-error/5'
                                        : 'border-warning bg-warning/5'
                                }`}
                            >
                                <p className="text-sm font-medium text-base-content">
                                    {flag.description}
                                </p>
                                <p className="mt-1 text-sm text-base-content/70 whitespace-normal">
                                    {flag.recommendedAction}
                                </p>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {(hypotheses?.length ?? 0) > 0 && (
                <section>
                    <div className="mb-2 flex items-center gap-2 text-sm font-medium text-primary">
                        <MdPsychology className="text-lg" />
                        Hipóteses diagnósticas
                    </div>
                    <ul className="flex flex-col gap-2">
                        {hypotheses!.map((hypothesis, index) => (
                            <li
                                key={index}
                                className="rounded-lg border border-base-300 p-3"
                            >
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-medium text-base-content">
                                        {hypothesis.description}
                                    </span>
                                    <span
                                        className={`badge badge-sm ${
                                            LIKELIHOOD_CLASS[hypothesis.likelihood] ??
                                            'badge-ghost'
                                        }`}
                                    >
                                        {LIKELIHOOD_LABEL[hypothesis.likelihood] ??
                                            hypothesis.likelihood}
                                    </span>
                                    {hypothesis.icdCode && (
                                        <span
                                            className="badge badge-sm badge-outline"
                                            title={hypothesis.icdDescription}
                                        >
                                            CID {hypothesis.icdCode}
                                        </span>
                                    )}
                                </div>

                                {hypothesis.supportingFindings?.length > 0 && (
                                    <p className="mt-2 text-sm text-base-content/70 whitespace-normal">
                                        <span className="text-success">A favor: </span>
                                        {hypothesis.supportingFindings.join('; ')}
                                    </p>
                                )}

                                {(hypothesis.againstFindings?.length ?? 0) > 0 && (
                                    <p className="mt-1 text-sm text-base-content/70 whitespace-normal">
                                        <span className="text-error">Contra: </span>
                                        {hypothesis.againstFindings!.join('; ')}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {(suggestedExams?.length ?? 0) > 0 && (
                <section>
                    <div className="mb-2 flex items-center gap-2 text-sm font-medium text-secondary">
                        <MdBiotech className="text-lg" />
                        Exames sugeridos para investigar
                    </div>
                    <ul className="flex flex-col gap-2">
                        {suggestedExams!.map((exam, index) => {
                            const picked = isPicked(exam)
                            return (
                                <li
                                    key={index}
                                    className="rounded-lg border border-base-300 p-3"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-medium text-base-content">
                                                    {exam.catalogName || exam.name}
                                                </span>
                                                {exam.priority === 'urgente' && (
                                                    <span className="badge badge-sm badge-error">
                                                        urgente
                                                    </span>
                                                )}
                                                {!exam.catalogId && (
                                                    <span
                                                        className="badge badge-sm badge-warning gap-1"
                                                        title="Não encontrado no catálogo desta conta — será preciso cadastrar"
                                                    >
                                                        <MdWarningAmber className="text-sm" />
                                                        fora do catálogo
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-1 text-sm text-base-content/70 whitespace-normal">
                                                {exam.rationale}
                                            </p>
                                        </div>

                                        {onPickExam && (
                                            <button
                                                type="button"
                                                className={`btn btn-xs shrink-0 gap-1 ${
                                                    picked
                                                        ? 'btn-success btn-outline'
                                                        : 'btn-outline'
                                                }`}
                                                onClick={() => onPickExam(exam)}
                                                title={
                                                    picked
                                                        ? 'Remover da lista do pedido'
                                                        : 'Marcar para o pedido de exames'
                                                }
                                            >
                                                {picked ? (
                                                    <>
                                                        <MdCheck className="text-sm" />
                                                        Adicionado
                                                    </>
                                                ) : (
                                                    <>
                                                        <MdAdd className="text-sm" />
                                                        Adicionar
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </section>
            )}
        </div>
    )
}
